import React from "react";
import { FiImage, FiPlus, FiSave } from "react-icons/fi";

import AdminButton from "./../../_shared/ui/AdminButton";
import AdminDrawer from "./../../_shared/ui/AdminDrawer";
import AdminField from "./../../_shared/ui/AdminField";

import { MEDIA_ALBUM_FORM_HELPERS } from "../data/mediaAlbumsAdminData";

export default function MediaAlbumFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
}) {
  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier l’album" : "Nouvel album photo"}
      subtitle={
        isEdit
          ? "Mettez à jour les informations principales de l’album."
          : "Créez un album puis ajoutez les photos depuis la médiathèque."
      }
      icon={FiImage}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <AdminField
            label="Titre de l’album"
            required
            error={fieldErrors.title}
            help="Exemple : Photos de la commune, Patrimoine, Vie locale..."
          >
            <input
              value={form.title}
              onChange={(event) => onChange("title", event.target.value)}
              placeholder="Titre affiché dans l’administration"
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>

          <AdminField
            label="Slug"
            help="Facultatif. S’il est vide, il sera généré automatiquement."
          >
            <input
              value={form.slug}
              onChange={(event) => onChange("slug", event.target.value)}
              placeholder="photos-de-la-commune"
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>

          <AdminField
            label="Description"
            help="Texte interne ou public pour expliquer le contenu de l’album."
          >
            <textarea
              value={form.description}
              onChange={(event) => onChange("description", event.target.value)}
              placeholder="Décrivez brièvement cet album..."
              rows={8}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-6 text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>

          <AdminField
            label="Ordre d’affichage"
            error={fieldErrors.display_order}
            help="Plus le chiffre est petit, plus l’album apparaît haut dans les listes."
          >
            <input
              type="number"
              min="0"
              value={form.display_order}
              onChange={(event) => onChange("display_order", event.target.value)}
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>
        </div>

        <aside className="space-y-5">
          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <div className="mt-3 space-y-3">
              <ToggleLine
                label="Album actif"
                helper={
                  form.is_active
                    ? "L’album peut être utilisé."
                    : "L’album est désactivé."
                }
                active={form.is_active}
                onClick={() => onChange("is_active", !form.is_active)}
              />

              <ToggleLine
                label="Album public"
                helper={
                  form.is_public
                    ? "Visible côté site public."
                    : "Réservé à l’administration."
                }
                active={form.is_public}
                onClick={() => onChange("is_public", !form.is_public)}
              />

              <ToggleLine
                label="Afficher sur l’accueil"
                helper={
                  form.show_on_home
                    ? "Utilisable pour le carrousel d’accueil."
                    : "Non utilisé sur la page d’accueil."
                }
                active={form.show_on_home}
                onClick={() => onChange("show_on_home", !form.show_on_home)}
              />
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-sky-100 bg-sky-50 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
            <p className="text-sm font-bold text-sky-900 dark:text-sky-200">
              Conseils
            </p>

            <ul className="mt-3 space-y-2">
              {MEDIA_ALBUM_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-sky-800 dark:text-sky-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}

function ToggleLine({ label, helper, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-left transition ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
      }`}
    >
      <span>
        <span className="block text-sm font-bold">{label}</span>
        <span className="mt-0.5 block text-xs opacity-75">{helper}</span>
      </span>

      <span
        className={`h-6 w-11 rounded-full p-1 transition ${
          active ? "bg-emerald-500" : "bg-slate-400"
        }`}
      >
        <span
          className={`block h-4 w-4 rounded-full bg-white transition ${
            active ? "translate-x-5" : ""
          }`}
        />
      </span>
    </button>
  );
}